export enum ReportType {
  SEARCH_TERM = "searchTerm",
  CATEGORY = "category",
}

export enum SearchTermsState {
  ALL = "all",
  HIDE = "hide",
  SAVE = "save",
}

export enum MutationOpType {
  CHANGE_STATE = "changeState",
  ADD_LABEL = "addLabel",
  REMOVE_LABEL = "removeLabel",
}

export enum SearchExploreFilterField {
  PRICE = "price",
  REVENUE = "revenue",
  REVIEW = "review",
}

export enum SearchExploreFilterMode {
  SHARE = "share",
  COUNT = "count",
}

export interface Label {
  _id: string
  color: string
  title: string
  description?: string
}

export interface SortSettings {
  minSales?: number
  maxSales?: number
  minRevenue?: number
  maxRevenue?: number
  minRank?: number
  maxRank?: number
  minReview?: number
  maxReview?: number
  minPrice?: number
  maxPrice?: number
  isMatchingFilterAsins?: boolean
  countMatchingFilterAsins?: number
  isOnlyShowMatchingAsins?: boolean
}

export interface ExploreFilter {
  _id?: string
  field: SearchExploreFilterField
  type: SearchExploreFilterMode
  threshold: number
  min: number
  max: number
}

export interface SettingsDoc {
  _id?: string
  profileId?: number
  marketplaceId?: string
  reportType: ReportType
  sort: SortSettings
  exploreFilters: ExploreFilter[]
  version: number
  updatedAt?: string
}

export interface TopClickedAsinPreview {
  asin: string
  clickShareLatest?: number
}

export interface TopProductPreview {
  asin: string
  rank?: number
  price?: { value: number; currency?: string }
  monthlySales?: number
  ratings_total?: number
  linkToAmazon?: string
}

export interface SearchTermRow {
  _id: string
  profileId: number
  marketplaceId: string
  searchTerm: string
  searchTermLower: string
  lastSearchFrequencyRank: number
  matchingCount: number
  state: SearchTermsState | null
  labelIds: string[]
  topClickedAsinsPreview: TopClickedAsinPreview[]
  sourceUpdatedAt?: string
  rebuildAt?: string
}

export interface CategoryRow {
  _id: string
  profileId: number
  marketplaceId: string
  categoryId: string
  categoryName: string
  categoryNameLower: string
  matchingCount: number
  state: SearchTermsState | null
  labelIds: string[]
  topProductsPreview?: TopProductPreview[]
  sourceUpdatedAt?: string
  rebuildAt?: string
}

export type ListRow = SearchTermRow | CategoryRow

export interface ListTotals {
  all?: number
  hide?: number
  save?: number
  [key: string]: number | undefined
}

export interface ListResponse<TRow extends ListRow = ListRow> {
  rows: TRow[]
  nextCursor: string | null
  totals: ListTotals
}

export interface MetaInfo {
  _id?: string
  profileId?: number
  marketplaceId?: string
  reportType?: ReportType
  totals?: ListTotals
  settingsVersion?: number
  lastRebuildStartedAt?: string
  lastRebuildCompletedAt?: string
  lastRebuildDurationMs?: number
}

export interface BootstrapResponse {
  list: ListResponse
  settings: SettingsDoc | null
  labels: Label[]
  meta: MetaInfo | null
}

export interface GetBootstrapArgs {
  reportType: ReportType
}

export interface GetListArgs {
  reportType: ReportType
  cursor?: string | null
  limit?: number
  state?: SearchTermsState | ""
  labelIds?: string[]
  search?: string
}

export interface GetDetailsArgs {
  reportType: ReportType
  searchTerm?: string
  categoryId?: string
  include?: string
}

export interface SearchTermTrendPoint {
  date: string
  value: number
}

export interface TopClickedAsinDetail {
  asin: string
  clickShareLatest?: number
  conversionShareLatest?: number
  clickShareTrends?: SearchTermTrendPoint[]
  conversionShareTrends?: SearchTermTrendPoint[]
}

export interface TopRankingAsinDetail {
  asin: string
  rank?: number
  price?: { value: number; currency?: string }
  monthlySales?: number
  ratings_total?: number
  linkToAmazon?: string
  revenue?: number
}

export interface SearchTermDetails {
  _id?: string
  searchTerm?: string
  searchFrequencyRankTrends?: SearchTermTrendPoint[]
  topClickedAsins?: TopClickedAsinDetail[]
  topRankingAsins?: TopRankingAsinDetail[]
}

export interface CategoryDetails {
  _id?: string
  categoryId?: string
  categoryName?: string
  topProducts?: TopRankingAsinDetail[]
}

export type DetailsResponse = SearchTermDetails | CategoryDetails

export interface ChangeStateOp {
  type: MutationOpType.CHANGE_STATE
  searchTerm?: string
  categoryId?: string
  state: SearchTermsState
}

export interface AddLabelOp {
  type: MutationOpType.ADD_LABEL
  searchTerm?: string
  categoryId?: string
  labelId: string
}

export interface RemoveLabelOp {
  type: MutationOpType.REMOVE_LABEL
  searchTerm?: string
  categoryId?: string
  labelId: string
}

export type MutationOp = ChangeStateOp | AddLabelOp | RemoveLabelOp

export interface ApplyMutationsArgs {
  reportType: ReportType
  ops: MutationOp[]
  listArgs?: GetListArgs
  bootstrapArgs?: GetBootstrapArgs
}

export interface ApplyMutationsResponse {
  ok: boolean
  applied: number
}

export interface SaveSettingsArgs {
  reportType: ReportType
  sort: SortSettings
  exploreFilters: ExploreFilter[]
}

export interface SaveSettingsResponse {
  ok: boolean
  settingsVersion: number
}

export interface DeleteExploreFilterArgs {
  reportType: ReportType
  id: string
}

export interface CreateLabelArgs {
  color: string
  title: string
  description?: string
}
